import React, { useMemo } from "react"
import { Row, Col, Card, CardBody, Badge, Button, Progress, Table } from "reactstrap"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { selectUser } from "store/features/auth/authSlice"
import {
  useGetAllProjectsQuery,
  useGetAllWorkLogsQuery,
  useGetAllClientsQuery,
} from "store/features/client/clientManagementApi"
import { useGetAllClientLeadsQuery } from "store/features/clientLeads/clientLeadsManagement"
import { useGetAllTargetsQuery } from "store/features/target/targetApi"
import { useGetPayrollQuery } from "store/features/attendance/attendanceApi"
import DashboardLoader from "components/Loader/DashboardLoader"

const DeveloperDashboard = () => {
  const user = useSelector(selectUser)
  const cleanRole = String(user?.role || "").toLowerCase().trim().replace(/[-\s]/g, "_")
  const isMarketing = cleanRole === "marketing"

  const currentMonthStr = new Date().toISOString().slice(0, 7) // e.g. "2026-08"
  const currentMonthName = new Date().toLocaleString("default", { month: "long", year: "numeric" })

  const { data: projects = [], isLoading: projectsLoading } = useGetAllProjectsQuery()
  const { data: workLogs = [], isLoading: workLogsLoading } = useGetAllWorkLogsQuery()
  const { data: targets = [], isLoading: targetsLoading } = useGetAllTargetsQuery()
  const { data: payrollData = [], isLoading: payrollLoading } = useGetPayrollQuery({ month: currentMonthStr })
  const { data: clients = [], isLoading: clientsLoading } = useGetAllClientsQuery()
  const { data: clientLeads = [], isLoading: clientLeadsLoading } = useGetAllClientLeadsQuery()

  const isLoading = projectsLoading || workLogsLoading || targetsLoading || payrollLoading || (isMarketing && (clientsLoading || clientLeadsLoading))

  // Helper to check if a user is assigned to a list/field
  const isUserAssigned = (assignedList, userId) => {
    if (!assignedList) return false
    const list = Array.isArray(assignedList) ? assignedList : [assignedList]
    return list.some(idOrUser => {
      if (typeof idOrUser === "object" && idOrUser !== null) {
        return String(idOrUser.id) === String(userId)
      }
      return String(idOrUser) === String(userId)
    })
  }

  // 1. Projects
  const myProjects = useMemo(() => {
    if (!user?.id || !Array.isArray(projects)) return []
    if (isMarketing) {
      // Marketing role can view all projects or filter to assigned ones
      return projects
    }
    return projects.filter(project =>
      isUserAssigned(project.assigned_frontend_dev, user.id) ||
      isUserAssigned(project.assigned_backend_dev, user.id) ||
      isUserAssigned(project.assigned_team, user.id) ||
      isUserAssigned(project.assigned_designer, user.id) ||
      isUserAssigned(project.assigned_project_manager, user.id)
    )
  }, [projects, user, isMarketing])

  const activeProjectsCount = useMemo(() => {
    return myProjects.filter(p => p.status === "In Progress" || p.status === "Pending").length
  }, [myProjects])

  const completedProjectsCount = useMemo(() => {
    return myProjects.filter(p => p.status === "Completed").length
  }, [myProjects])

  // 2. My Work Logs (for developer/designer)
  const myWorkLogs = useMemo(() => {
    if (!user?.id || !Array.isArray(workLogs)) return []
    return workLogs.filter(log => String(log.employer_id) === String(user.id))
  }, [workLogs, user])

  // Work log hours this month
  const thisMonthHours = useMemo(() => {
    return myWorkLogs
      .filter(log => log.date && log.date.startsWith(currentMonthStr))
      .reduce((sum, log) => sum + (parseFloat(log.hours_spent) || 0), 0)
  }, [myWorkLogs, currentMonthStr])

  // 3. My Monthly Target & Achieved
  const myTargetInfo = useMemo(() => {
    const userTargetObj = targets.find(
      t => t.month === currentMonthStr && (String(t.user) === String(user?.id) || String(t.user?.id) === String(user?.id))
    )
    const targetAmount = userTargetObj ? parseFloat(userTargetObj.target_amount) || 0 : 0

    // Calculate achieved from projects
    let totalAchieved = 0
    const projectBreakdown = []

    projects.forEach(project => {
      const pDate = project.start_date || project.created_at || project.systemdate
      const projectStartMonth = (pDate && typeof pDate === "string") ? pDate.slice(0, 7) : currentMonthStr

      if (projectStartMonth <= currentMonthStr) {
        const isMarketingAssigned =
          isUserAssigned(project.assigned_marketing_officer, user?.id) ||
          String(project.marketing_officer_id) === String(user?.id)

        if (isMarketing) {
          if (isMarketingAssigned) {
            const projectBudget = parseFloat(project.budget) || 0
            totalAchieved += projectBudget
            projectBreakdown.push({
              id: project.id,
              name: project.name,
              progress: 100,
              share: projectBudget,
              achieved: projectBudget,
            })
          }
        } else {
          const isDevAssigned =
            isUserAssigned(project.assigned_frontend_dev, user?.id) ||
            isUserAssigned(project.assigned_backend_dev, user?.id) ||
            isUserAssigned(project.assigned_designer, user?.id) ||
            isUserAssigned(project.assigned_team, user?.id) ||
            isUserAssigned(project.assigned_project_manager, user?.id)

          if (isDevAssigned) {
            const memberIds = new Set()
            const addList = list => {
              if (!list || !Array.isArray(list)) return
              list.forEach(item => {
                const mId = typeof item === "object" && item !== null ? item.id : item
                if (mId) memberIds.add(String(mId))
              })
            }
            addList(project.assigned_frontend_dev)
            addList(project.assigned_backend_dev)
            addList(project.assigned_designer)
            addList(project.assigned_team)
            addList(project.assigned_project_manager)

            const N = memberIds.size || 1
            const projectBudget = parseFloat(project.budget) || 0
            const memberBudgetShare = projectBudget / N

            let devProgressData = project.developer_progress || {}
            if (typeof devProgressData === "string") {
              try {
                devProgressData = JSON.parse(devProgressData)
              } catch (e) {
                devProgressData = {}
              }
            }

            const currentDevProgress = Number(devProgressData[user?.id] ?? devProgressData[String(user?.id)] ?? 0)
            const displayProgressPercent = Math.round(currentDevProgress)
            const memberAchieved = (memberBudgetShare * displayProgressPercent) / 100

            totalAchieved += memberAchieved
            projectBreakdown.push({
              id: project.id,
              name: project.name,
              progress: displayProgressPercent,
              share: memberBudgetShare,
              achieved: memberAchieved,
            })
          }
        }
      }
    })

    const achievementPercent = targetAmount > 0 ? Math.min(100, Math.round((totalAchieved / targetAmount) * 100)) : 0
    return {
      targetAmount,
      totalAchieved,
      achievementPercent,
      projectBreakdown,
    }
  }, [targets, currentMonthStr, user, projects, isMarketing])

  // 4. My Payroll
  const myPayroll = useMemo(() => {
    if (!Array.isArray(payrollData) || !user?.id) return null
    return payrollData.find(p => String(p.user?.id || p.user) === String(user.id))
  }, [payrollData, user])

  const getPriorityBadge = priority => {
    switch (priority) {
      case "High":
      case 1:
        return <Badge color="danger" className="px-2 py-1">High</Badge>
      case "Medium":
      case 2:
        return <Badge color="warning" className="px-2 py-1">Medium</Badge>
      case "Low":
      case 3:
        return <Badge color="info" className="px-2 py-1">Low</Badge>
      default:
        return <Badge color="secondary" className="px-2 py-1">{priority || "Normal"}</Badge>
    }
  }

  const getStatusBadge = status => {
    switch (status) {
      case "Completed":
        return <Badge color="success" className="px-2 py-1">Completed</Badge>
      case "In Progress":
        return <Badge color="primary" className="px-2 py-1">In Progress</Badge>
      case "Pending":
        return <Badge color="warning" className="px-2 py-1">Pending</Badge>
      case "Blocked":
      case "On Hold":
        return <Badge color="danger" className="px-2 py-1">{status}</Badge>
      default:
        return <Badge color="secondary" className="px-2 py-1">{status || "New"}</Badge>
    }
  }

  if (isLoading) return <DashboardLoader />

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* WELCOME BANNER */}
        <Row className="mb-4">
          <Col lg={12}>
            <Card className="border-0 shadow-sm overflow-hidden" style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)" }}>
              <CardBody className="p-4 text-white">
                <Row className="align-items-center">
                  <Col md={8}>
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white fs-3 fw-bold shadow"
                        style={{ width: "56px", height: "56px" }}
                      >
                        {(user?.nickname || user?.name || "U")[0]?.toUpperCase()}
                      </div>
                      <div>
                        <h4 className="text-white mb-1 fw-bold">
                          Welcome back, {user?.nickname || user?.name || "Team Member"}! 👋
                        </h4>
                        <p className="text-light text-opacity-75 mb-0 fs-13">
                          Role: <span className="badge bg-primary text-capitalize me-2">{user?.role || "Staff"}</span>
                          Month: <span className="badge bg-secondary">{currentMonthName}</span>
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col md={4} className="text-md-end mt-3 mt-md-0">
                    <div className="d-flex gap-2 justify-content-md-end">
                      {isMarketing ? (
                        <>
                          <Link to="/all-leads" className="btn btn-primary btn-sm px-3 shadow-sm">
                            <i className="fas fa-user-plus me-1" /> All Leads ({clientLeads.length})
                          </Link>
                          <Link to="/all-clients" className="btn btn-outline-light btn-sm px-3">
                            <i className="fas fa-handshake me-1" /> All Clients ({clients.length})
                          </Link>
                        </>
                      ) : (
                        <>
                          <Link to="/worklog" className="btn btn-primary btn-sm px-3 shadow-sm">
                            <i className="fas fa-plus-circle me-1" /> Log Work
                          </Link>
                          <Link to="/all-projects" className="btn btn-outline-light btn-sm px-3">
                            <i className="fas fa-project-diagram me-1" /> My Projects
                          </Link>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* 4 STAT CARDS */}
        <Row className="mb-4">
          {/* Card 1: Projects */}
          <Col xl={3} md={6}>
            <Card className="mini-stats-wid border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-2 fs-13">{isMarketing ? "Total Projects" : "Assigned Projects"}</p>
                    <h4 className="mb-1 fw-bold text-primary">{myProjects.length}</h4>
                    <div className="text-muted fs-12">
                      <span className="text-success fw-semibold me-2">
                        <i className="fas fa-spinner me-1" />{activeProjectsCount} Active
                      </span>
                      <span className="text-muted">
                        <i className="fas fa-check me-1" />{completedProjectsCount} Done
                      </span>
                    </div>
                  </div>
                  <div className="mini-stat-icon avatar-sm rounded-circle bg-primary bg-opacity-10 align-self-center">
                    <span className="avatar-title bg-transparent text-primary fs-4">
                      <i className="fas fa-project-diagram" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Card 2: Leads / Work Hours */}
          <Col xl={3} md={6}>
            <Card className="mini-stats-wid border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-2 fs-13">
                      {isMarketing ? "Total Leads" : `Work Hours (${currentMonthName.slice(0, 3)})`}
                    </p>
                    <h4 className="mb-1 fw-bold text-success">
                      {isMarketing ? clientLeads.length : `${thisMonthHours.toFixed(1)} hrs`}
                    </h4>
                    <div className="text-muted fs-12">
                      {isMarketing ? (
                        <span className="text-primary fw-semibold">{clients.length} Converted Clients</span>
                      ) : (
                        <span className="text-muted">{myWorkLogs.length} total work log entries</span>
                      )}
                    </div>
                  </div>
                  <div className="mini-stat-icon avatar-sm rounded-circle bg-success bg-opacity-10 align-self-center">
                    <span className="avatar-title bg-transparent text-success fs-4">
                      <i className={isMarketing ? "fas fa-user-plus" : "fas fa-clock"} />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Card 3: Target Achievement */}
          <Col xl={3} md={6}>
            <Card className="mini-stats-wid border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-2 fs-13">My Target Progress</p>
                    <h4 className="mb-1 fw-bold text-warning">{myTargetInfo.achievementPercent}%</h4>
                    <div className="text-muted fs-12">
                      <span>৳{Math.round(myTargetInfo.totalAchieved).toLocaleString()}</span>
                      {myTargetInfo.targetAmount > 0 && <span> / ৳{myTargetInfo.targetAmount.toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="mini-stat-icon avatar-sm rounded-circle bg-warning bg-opacity-10 align-self-center">
                    <span className="avatar-title bg-transparent text-warning fs-4">
                      <i className="fas fa-bullseye" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Card 4: Payroll & Salary */}
          <Col xl={3} md={6}>
            <Card className="mini-stats-wid border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-2 fs-13">My Salary Status</p>
                    <h4 className="mb-1 fw-bold text-info">
                      {myPayroll ? `৳${parseFloat(myPayroll.net_salary || myPayroll.base_salary || 0).toLocaleString()}` : "৳ 0"}
                    </h4>
                    <div className="text-muted fs-12">
                      <Badge color={myPayroll?.payment_status === "Paid" ? "success" : "warning"} className="px-2 py-1">
                        {myPayroll?.payment_status || "Pending"}
                      </Badge>
                      <Link to="/payroll" className="ms-2 text-primary text-decoration-none">
                        View Details &rarr;
                      </Link>
                    </div>
                  </div>
                  <div className="mini-stat-icon avatar-sm rounded-circle bg-info bg-opacity-10 align-self-center">
                    <span className="avatar-title bg-transparent text-info fs-4">
                      <i className="fas fa-file-invoice-dollar" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* MAIN BODY: PROJECTS & LEADS / TARGET BREAKDOWN */}
        <Row>
          {/* LEFT: Projects & Leads */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h5 className="card-title mb-1 fw-bold">{isMarketing ? "Projects Overview" : "My Assigned Projects"}</h5>
                    <p className="text-muted mb-0 fs-12">
                      {isMarketing ? "All active CRM projects and their statuses" : "Projects you are actively assigned to as a developer"}
                    </p>
                  </div>
                  <Link to="/all-projects" className="btn btn-outline-primary btn-sm">
                    View All ({myProjects.length})
                  </Link>
                </div>

                <div className="table-responsive">
                  <Table hover className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Project Name</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th style={{ width: "180px" }}>Progress</th>
                        <th>Deadline</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myProjects.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4 text-muted">
                            <i className="fas fa-folder-open fs-3 d-block mb-2 text-muted" />
                            No projects found.
                          </td>
                        </tr>
                      ) : (
                        myProjects.slice(0, 6).map(project => {
                          const prog = Number(project.progress || 0)

                          return (
                            <tr key={project.id}>
                              <td>
                                <div className="fw-semibold text-dark">{project.name}</div>
                                <small className="text-muted">{project.client_name || "Internal"}</small>
                              </td>
                              <td>{getPriorityBadge(project.priority_status)}</td>
                              <td>{getStatusBadge(project.status)}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <Progress
                                    value={prog}
                                    color={prog >= 100 ? "success" : prog > 50 ? "primary" : "warning"}
                                    className="flex-grow-1"
                                    style={{ height: "6px" }}
                                  />
                                  <span className="fs-12 fw-bold text-muted">{prog}%</span>
                                </div>
                              </td>
                              <td className="fs-12 text-muted">
                                {project.end_date ? project.end_date.slice(0, 10) : "No deadline"}
                              </td>
                              <td>
                                <Link to={`/projects/${project.id}`} className="btn btn-light btn-sm">
                                  <i className="fas fa-eye text-primary" />
                                </Link>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>

            {/* SECOND WIDGET: LEADS FOR MARKETING OR WORKLOGS FOR DEVELOPER */}
            {isMarketing ? (
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h5 className="card-title mb-1 fw-bold">Recent Leads</h5>
                      <p className="text-muted mb-0 fs-12">Latest potential client inquiries and statuses</p>
                    </div>
                    <Link to="/all-leads" className="btn btn-outline-primary btn-sm">
                      View All Leads ({clientLeads.length})
                    </Link>
                  </div>

                  <div className="table-responsive">
                    <Table hover className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Lead Name</th>
                          <th>Company / Email</th>
                          <th>Phone</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientLeads.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                              <i className="fas fa-user-plus fs-3 d-block mb-2 text-muted" />
                              No leads recorded yet.
                            </td>
                          </tr>
                        ) : (
                          clientLeads.slice(0, 5).map(lead => (
                            <tr key={lead.id}>
                              <td>
                                <div className="fw-semibold text-dark">{lead.name}</div>
                              </td>
                              <td>
                                <div className="fs-13 text-dark">{lead.company || "N/A"}</div>
                                <small className="text-muted">{lead.email || "No email"}</small>
                              </td>
                              <td className="fs-13 text-muted">{lead.phone || "N/A"}</td>
                              <td>{getPriorityBadge(lead.priority_status)}</td>
                              <td>{getStatusBadge(lead.status)}</td>
                              <td>
                                <Link to={`/leads/${lead.id}`} className="btn btn-light btn-sm">
                                  <i className="fas fa-eye text-primary" />
                                </Link>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h5 className="card-title mb-1 fw-bold">Recent Work Logs</h5>
                      <p className="text-muted mb-0 fs-12">Your recently submitted daily work entries</p>
                    </div>
                    <Link to="/worklog" className="btn btn-outline-primary btn-sm">
                      View All ({myWorkLogs.length})
                    </Link>
                  </div>

                  <div className="table-responsive">
                    <Table hover className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Work Summary</th>
                          <th>Hours</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myWorkLogs.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center py-4 text-muted">
                              <i className="fas fa-clipboard-list fs-3 d-block mb-2 text-muted" />
                              No work logs recorded yet. Click "Log Work" to create your first entry.
                            </td>
                          </tr>
                        ) : (
                          myWorkLogs.slice(0, 5).map(log => (
                            <tr key={log.id}>
                              <td className="text-nowrap">
                                <code className="text-dark">{log.date || "N/A"}</code>
                              </td>
                              <td>
                                <div className="fw-medium text-dark text-truncate" style={{ maxWidth: "350px" }}>
                                  {log.work_summary}
                                </div>
                                {log.title && <small className="text-muted">{log.title}</small>}
                              </td>
                              <td>
                                <Badge color="info" className="px-2 py-1">
                                  {log.hours_spent} hrs
                                </Badge>
                              </td>
                              <td>{getStatusBadge(log.work_status)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>

          {/* RIGHT: TARGET PROGRESS & QUICK SUMMARY */}
          <Col lg={4}>
            {/* Target Breakdown Card */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="card-title mb-0 fw-bold">My Target Progress</h5>
                  <Link to="/targets" className="text-primary fs-12 fw-semibold">
                    Details &rarr;
                  </Link>
                </div>

                <div className="p-3 bg-light rounded-3 mb-3 text-center">
                  <div className="text-muted fs-12 mb-1">Target for {currentMonthName}</div>
                  <h3 className="fw-bold text-primary mb-1">
                    ৳{myTargetInfo.targetAmount.toLocaleString()}
                  </h3>
                  <div className="text-muted fs-12 mb-3">
                    Achieved: <span className="fw-bold text-success">৳{Math.round(myTargetInfo.totalAchieved).toLocaleString()}</span>
                  </div>
                  <Progress
                    value={myTargetInfo.achievementPercent}
                    color={myTargetInfo.achievementPercent >= 100 ? "success" : "primary"}
                    className="mb-2"
                    style={{ height: "8px" }}
                  />
                  <div className="d-flex justify-content-between text-muted fs-11">
                    <span>Progress</span>
                    <span className="fw-bold text-dark">{myTargetInfo.achievementPercent}%</span>
                  </div>
                </div>

                <h6 className="fs-12 fw-bold text-uppercase text-muted mb-2">Project Contributions</h6>
                <div className="list-group list-group-flush">
                  {myTargetInfo.projectBreakdown.length === 0 ? (
                    <div className="text-muted fs-12 text-center py-2">No project contributions recorded yet</div>
                  ) : (
                    myTargetInfo.projectBreakdown.slice(0, 4).map(p => (
                      <div key={p.id} className="list-group-item px-0 py-2 border-bottom d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-medium fs-13 text-dark text-truncate" style={{ maxWidth: "160px" }}>{p.name}</div>
                          <small className="text-muted">Progress: {p.progress}%</small>
                        </div>
                        <div className="text-end">
                          <span className="text-success fw-bold fs-12">+৳{Math.round(p.achieved).toLocaleString()}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Quick Profile Summary */}
            <Card className="border-0 shadow-sm">
              <CardBody>
                <h5 className="card-title mb-3 fw-bold">My Info & Quick Links</h5>
                <div className="d-flex flex-column gap-2 fs-13 mb-3">
                  <div className="d-flex justify-content-between py-1 border-bottom">
                    <span className="text-muted">Email:</span>
                    <span className="fw-medium text-dark">{user?.email || user?.personal_email || "N/A"}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1 border-bottom">
                    <span className="text-muted">Phone:</span>
                    <span className="fw-medium text-dark">{user?.phone || "N/A"}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1 border-bottom">
                    <span className="text-muted">Role:</span>
                    <span className="badge bg-light text-dark text-capitalize">{user?.role || "Team Member"}</span>
                  </div>
                </div>

                <div className="d-grid gap-2">
                  {isMarketing && (
                    <>
                      <Link to="/all-leads" className="btn btn-light btn-sm text-start d-flex justify-content-between align-items-center">
                        <span><i className="fas fa-user-plus me-2 text-primary" /> All Leads</span>
                        <i className="fas fa-chevron-right fs-11 text-muted" />
                      </Link>
                      <Link to="/all-clients" className="btn btn-light btn-sm text-start d-flex justify-content-between align-items-center">
                        <span><i className="fas fa-handshake me-2 text-success" /> All Clients</span>
                        <i className="fas fa-chevron-right fs-11 text-muted" />
                      </Link>
                      <Link to="/all-projects" className="btn btn-light btn-sm text-start d-flex justify-content-between align-items-center">
                        <span><i className="fas fa-project-diagram me-2 text-info" /> All Projects</span>
                        <i className="fas fa-chevron-right fs-11 text-muted" />
                      </Link>
                    </>
                  )}
                  <Link to="/targets" className="btn btn-light btn-sm text-start d-flex justify-content-between align-items-center">
                    <span><i className="fas fa-bullseye me-2 text-warning" /> My Target & Progress</span>
                    <i className="fas fa-chevron-right fs-11 text-muted" />
                  </Link>
                  <Link to="/payroll" className="btn btn-light btn-sm text-start d-flex justify-content-between align-items-center">
                    <span><i className="fas fa-file-invoice-dollar me-2 text-info" /> My Payroll & Payslip</span>
                    <i className="fas fa-chevron-right fs-11 text-muted" />
                  </Link>
                  <Link to="/profile" className="btn btn-light btn-sm text-start d-flex justify-content-between align-items-center">
                    <span><i className="fas fa-user-cog me-2 text-primary" /> Profile Settings</span>
                    <i className="fas fa-chevron-right fs-11 text-muted" />
                  </Link>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default DeveloperDashboard
